import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FindAllSsoProvidersResponseDto, PrismaSSOProviderDto, SsoProviderService } from 'app/API';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { ssoProvidersKeys, TSsoProvidersListParams } from 'app/queries/ssoProviders/ssoProvidersKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import toast from 'react-hot-toast';
import { fetchAllSsoProvidersForExport } from '../api/fetchAllSsoProvidersForExport';
import { createFetchSsoProvidersQueryFn } from '../api/fetchSsoProviders';
import { downloadSsoProvidersCsv } from '../helpers/downloadSsoProvidersCsv';
import { shouldNotifySsoProvidersError } from '../helpers/shouldNotifySsoProvidersError';

const SEARCH_DEBOUNCE_MS = 300;

type TState = {
  ssoProviders: FindAllSsoProvidersResponseDto;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (v: number) => void;
  search: string;
  setSearch: (v: string) => void;
  isLoading: boolean;
  hasInitialError: boolean;
  retryProviders: () => void;
  exportCSV: () => void;
  openCreateModal: () => void;
  openEditModal: (ssoProvider: PrismaSSOProviderDto) => void;
  removeProvider: (ssoProvider: PrismaSSOProviderDto) => void;
};

const PageContext = createContext<TState>({
  ssoProviders: { data: [], count: 0 },
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => {},
  search: '',
  setSearch: () => {},
  isLoading: false,
  hasInitialError: false,
  retryProviders: () => {},
  exportCSV: () => {},
  openCreateModal: () => {},
  openEditModal: () => {},
  removeProvider: () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { openModal } = useModalContext();
  const { page, pageSize, search, orderBy, orderType, setSearch, setPage, setPageSize } = useListQueryParams({
    defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    replaceSearch: true,
  });

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const isSearchSettling = search !== debouncedSearch;
  const listParams = useMemo<TSsoProvidersListParams>(
    () => ({ page, pageSize, orderBy, orderType, search: debouncedSearch }),
    [page, pageSize, orderBy, orderType, debouncedSearch],
  );

  const providersQuery = useQuery({
    queryKey: ssoProvidersKeys.list(listParams),
    queryFn: createFetchSsoProvidersQueryFn(call),
    placeholderData: keepPreviousData,
    enabled: !isSearchSettling,
  });

  const removeMutation = useMutation({
    mutationFn: ({ ssoProviderId }: { ssoProviderId: string; identityProvider: string }) =>
      call(() => SsoProviderService.remove({ ssoProviderId })),
    onSettled: async (_data, error, { identityProvider }) => {
      await queryClient.invalidateQueries({ queryKey: ssoProvidersKeys.lists() });
      if (error === null) {
        toast.success(`SSO provider ${identityProvider} has been removed.`);
      }
    },
    onError: (error) => {
      if (shouldNotifySsoProvidersError(error)) {
        toast.error('Unable to remove the SSO provider.');
      }
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => fetchAllSsoProvidersForExport(call, debouncedSearch),
    onSuccess: ({ data }) => {
      downloadSsoProvidersCsv(data);
    },
    onError: (error) => {
      if (shouldNotifySsoProvidersError(error)) {
        toast.error('Unable to export SSO providers.');
      }
    },
  });

  const openCreateModal = () => {
    openModal(ModalNamesEnum.SsoProviderModal, {});
  };

  const openEditModal = (ssoProvider: PrismaSSOProviderDto) => {
    openModal(ModalNamesEnum.SsoProviderModal, { ssoProvider });
  };

  const removeProvider = (ssoProvider: PrismaSSOProviderDto) => {
    if (!window.confirm(`Remove SSO provider ${ssoProvider.identityProvider}? Its users will lose SSO routing.`)) {
      return;
    }
    removeMutation.mutate({ ssoProviderId: ssoProvider.id, identityProvider: ssoProvider.identityProvider });
  };

  const ssoProviders = providersQuery.data ?? { data: [], count: 0 };
  const isLoading =
    providersQuery.isFetching || isSearchSettling || removeMutation.isPending || exportMutation.isPending;
  const hasInitialError = providersQuery.isError && providersQuery.data === undefined;
  const retryProviders = () => {
    void providersQuery.refetch();
  };

  return (
    <PageContext.Provider
      value={{
        ssoProviders,
        page,
        setPage,
        pageSize,
        setPageSize,
        search,
        setSearch,
        isLoading,
        hasInitialError,
        retryProviders,
        exportCSV: () => {
          if (!exportMutation.isPending) {
            exportMutation.mutate();
          }
        },
        openCreateModal,
        openEditModal,
        removeProvider,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
